/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library for date and time formatting.
import _ from 'underscore' // Npm: utility module.
import { useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI DataGrid component.
import {
  Button,
  Flex,
  Box,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import MccUpsert from 'components/MccUpsert'
import MccDropDownOptions from 'components/MccDropDownOptions'
import MccDelete from 'components/MccDelete'
import { MemoizedInput } from 'components/MemoizedInput'

/*
 * STYLES
 */
import { buttonStyle } from './index.style'

/*
 * GRAPHS
 */
import MccReadQuery from './__query__/index.mcc.read.query'

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })

  // Hook assignment .
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })

  // Hook assignment - Search states.
  const [mccToSearch, setMccToSearch] = React.useState('')
  const [dialingCodeToSearch, setDialingCodeToSearch] = React.useState('')
  const [countryNameToSearch, setCountryNameToSearch] = React.useState('')

  // Hook assignment .
  const { 'isOpen': isMccCreateOpen, 'onOpen': onMccCreateOpen, 'onClose': onMccCreateClose } = useDisclosure()
  const { 'isOpen': isMccUpdateOpen, 'onOpen': onMccUpdateOpen, 'onClose': onMccUpdateClose } = useDisclosure()
  const { 'isOpen': isMccDeleteOpen, 'onOpen': onMccDeleteOpen, 'onClose': onMccDeleteClose } = useDisclosure()

  const _QueryMccRead = useQuery(MccReadQuery, {
    'variables': {
      mccToSearch,
      dialingCodeToSearch,
      countryNameToSearch,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const { refetch } = _QueryMccRead
  // Ref assignment - Track first load completion and table header height.
  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)

  // Responsive view detection.
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Debounce handlers .
  const _onMccInputChange = React.useRef(_.debounce((e) => setMccToSearch(e.target.value), 800)).current
  const _onDialingCodeInputChange = React.useRef(_.debounce((e) => setDialingCodeToSearch(e.target.value), 800)).current
  const _onCountryNameInputChange = React.useRef(_.debounce((e) => setCountryNameToSearch(e.target.value), 800)).current

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onMccInputChange.cancel?.()
      _onDialingCodeInputChange.cancel?.()
      _onCountryNameInputChange.cancel?.()
    }
  }, [])

  // Data assignment - Check if first load is completed.
  if (!_QueryMccRead.loading && 0 < _QueryMccRead.data?.MccRead?.length) {
    _isFirstLoadCompleted.current = true
  }

  if (0 === _QueryMccRead.data?.MccRead?.length ||
    (0 < _QueryMccRead.data?.MccRead?.length &&
      !_.every(_.pluck(_QueryMccRead.data?.MccRead, 'status'), i => _successFlags?.includes(i)))) {
    _isFirstLoadCompleted.current = false
  }

  // Columns definition .
  const columns = React.useMemo(() => [
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'dialingCode',
      'headerName': 'Dialing Code',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100
    },
    {
      'field': 'mcc',
      'headerName': 'Mcc',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100
    },
    {
      'field': 'countryName',
      'headerName': 'Country',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'minOperatorPrefixLength',
      'headerName': 'MinOperatorPrefixLength',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'maxOperatorPrefixLength',
      'headerName': 'MaxOperatorPrefixLength',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <MccDropDownOptions
          mccId={row.id}
          onMccUpdate={onMccUpdateOpen}
          onMccDelete={onMccDeleteOpen}
        />
      )
    }
  ], [])

  // Rows mapping .
  const rows = React.useMemo(() =>
    _.compact(
      _QueryMccRead.data?.MccRead?.map((item, index) =>
        _successFlags.includes(item?.status)
          ? {
            'id': item?.id || String.random(9),
            'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
            'dialingCode': item?.dialingCode ?? '-',
            'mcc': item?.mcc ?? '-',
            'countryName': item?.countryName ?? '-',
            'minOperatorPrefixLength': item?.minOperatorPrefixLength ?? '-',
            'maxOperatorPrefixLength': item?.maxOperatorPrefixLength ?? '-',
            'createdAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
            'updatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD')
          }
          : null
      )
    ) || [],
    [_QueryMccRead.data?.MccRead, paginationModel.page, paginationModel.pageSize]
  )

  // Return component.
  return (
    <>
      <Flex className='mcc base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '14px'}
          bg='white'
          minH={_isCurrentViewMobile ? '130vh' : '99vh'}
          p={_isCurrentViewMobile ? '12px' : '22px'}>

          {/* Header Section */}
          <Flex pb='0px' justify='space-between' overflow='hidden' flexWrap={{ 'base': 'wrap', 'md': 'wrap' }}>
            <Flex gap='12px'>
              <Tooltip label='Create New MCC' fontSize='sm'>
                <Button
                  onClick={onMccCreateOpen}
                  leftIcon={<IoMdAdd />}
                  style={buttonStyle}>
                  Create Mcc
                </Button>
              </Tooltip>
            </Flex>
            <Flex gap='12px'>
              <DownloadToExcel
                cellsData={0 === _QueryMccRead.data?.MccRead?.length || !_.every(_.pluck(_QueryMccRead.data?.MccRead, 'status'), i => 'READ_SUCCESSFUL' === i) ? [] : _QueryMccRead.data?.MccRead?.map((item, __index) => ({
                  'S.No.': `${(__index + (paginationModel.pageSize * paginationModel.page)) + 1}.`,
                  'Dialing Code': item?.dialingCode ?? '-',
                  'Mcc': item?.mcc ?? '-',
                  'Country': item?.countryName ?? '-',
                  'MinOperatorPrefixLength': item?.minOperatorPrefixLength ?? '-',
                  'MaxOperatorPrefixLength': item?.maxOperatorPrefixLength ?? '-',
                  'CreatedAt': Moment(item.createdAt).format('YYYY-MM-DD'),
                  'UpdatedAt': Moment(item.updatedAt).format('YYYY-MM-DD')
                }))}
                headersData={[
                  'S.No.',
                  'Dialing Code',
                  'Mcc',
                  'Country',
                  'MinOperatorPrefixLength',
                  'MaxOperatorPrefixLength',
                  'CreatedAt',
                  'UpdatedAt'
                ].map(i => ({ 'key': i, 'label': i }))}
              />
            </Flex>
          </Flex>

          {/* Filter Section*/}
          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='10px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='flex-start'
          >
            <Box minW='150px' w='200px'>
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Dialing Code</Text>
              <MemoizedInput
                placeholder='Search by Dialing Code'
                className='filter searchInput'
                defaultValue={dialingCodeToSearch}
                onChange={_onDialingCodeInputChange}
              />
            </Box>

            <Box minW='150px' w='200px'>
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Mcc</Text>
              <MemoizedInput
                placeholder='Search by Mcc'
                className='filter searchInput'
                defaultValue={mccToSearch}
                onChange={_onMccInputChange}
              />
            </Box>

            <Box minW='150px' w='200px'>
              <Text fontSize='14px' fontWeight={600} color='rgb(141, 151, 181)'>Country Name</Text>
              <MemoizedInput
                placeholder='Search by Country'
                className='filter searchInput'
                defaultValue={countryNameToSearch}
                onChange={_onCountryNameInputChange}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid  */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.first(_QueryMccRead?.data?.MccRead)?._totalCount ?? 0}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
                />
              ),
              'noResultsOverlay': () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
                />
              ),
              'noRowsOverlay': () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight}
                />
              )
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryMccRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='Mcc Create'
          isOpen={isMccCreateOpen}
          onClose={onMccCreateClose}>
          <MccUpsert isCreateOnly={true} refetch={refetch} />
        </Modal>
        <Modal
          size='md'
          title='Mcc Update'
          isOpen={isMccUpdateOpen}
          onClose={onMccUpdateClose}>
          <MccUpsert refetch={refetch} />
        </Modal>
        <Modal
          size='md'
          title='Mcc Delete'
          isOpen={isMccDeleteOpen}
          onClose={onMccDeleteClose}>
          <MccDelete refetch={refetch} />
        </Modal>
      </Flex>
    </>
  )
}

/*
 * PROPTYPES
 */
Index.propTypes = {}

/*
 * EXPORTS
 */
export default Index
